import { convertColumnMetric } from '../types/dbt';
import { type CompiledTable } from '../types/explore';
import {
    DimensionType,
    FieldType,
    MetricType,
    type Metric,
} from '../types/field';
import {
    isPeriodOverPeriodAdditionalMetric,
    type AdditionalMetric,
} from '../types/metricQuery';
import type {
    CompiledRelation,
    CompiledRelationJoin,
} from '../types/relation/relations';
import { getTargetTableName } from './audiences';

type ConvertAdditionalMetricArgs = {
    additionalMetric: AdditionalMetric;
    table: CompiledTable;
};

export const convertAdditionalMetric = ({
    additionalMetric,
    table,
}: ConvertAdditionalMetricArgs): Metric => {
    const metric = convertColumnMetric({
        modelName: table.name,
        dimensionSql: additionalMetric.sql,
        name: additionalMetric.name,
        metric: { ...additionalMetric, filters: undefined },
        tableLabel: table.label,
    });

    const popMetadata = isPeriodOverPeriodAdditionalMetric(additionalMetric)
        ? additionalMetric
        : undefined;

    return {
        ...metric,
        ...(additionalMetric.filters && {
            filters: additionalMetric.filters,
        }),
        ...(additionalMetric.formatOptions && {
            formatOptions: additionalMetric.formatOptions,
        }),
        ...(popMetadata ?? {}),
        ...(additionalMetric.bucketWeightsByName && {
            bucketWeightsByName: additionalMetric.bucketWeightsByName,
        }),
        ...(additionalMetric.keepTogetherDimensionReference && {
            keepTogetherDimensionReference:
                additionalMetric.keepTogetherDimensionReference,
        }),
        fieldType: FieldType.METRIC,
    };
};

export const getPathBetweenTables = (
    sourceTable: string,
    targetTable: string,
    joinedTables: CompiledRelationJoin[] | undefined,
): string[] => {
    const graph: Record<string, string[]> = {};

    // Build bidirectional graph from joins
    for (const joinRelation of Object.values(joinedTables || [])) {
        const source = joinRelation.joinDetails.sourceTable;
        const target = getTargetTableName(joinRelation.joinDetails);

        if (!graph[source]) graph[source] = [];
        if (!graph[target]) graph[target] = [];

        graph[source].push(target);
        graph[target].push(source);
    }

    // BFS to find shortest path
    const queue: Array<{ table: string; path: string[] }> = [];
    const visited = new Set<string>();

    queue.push({ table: sourceTable, path: [sourceTable] });
    visited.add(sourceTable);

    while (queue.length > 0) {
        const { table, path } = queue.shift()!;

        if (table === targetTable) {
            return path;
        }

        for (const nextTable of graph[table] || []) {
            if (!visited.has(nextTable)) {
                visited.add(nextTable);
                queue.push({
                    table: nextTable,
                    path: [...path, nextTable],
                });
            }
        }
    }

    return []; // Return empty array if no path found
};

export const getCustomMetricType = (type: DimensionType): MetricType[] => {
    switch (type) {
        case DimensionType.STRING:
        case DimensionType.TIMESTAMP:
        case DimensionType.DATE:
            return [
                MetricType.COUNT_DISTINCT,
                MetricType.COUNT,
                MetricType.MIN,
                MetricType.MAX,
            ];
        case DimensionType.NUMBER:
            return [
                MetricType.MIN,
                MetricType.MAX,
                MetricType.SUM,
                MetricType.PERCENTILE,
                MetricType.MEDIAN,
                MetricType.AVERAGE,
                MetricType.COUNT_DISTINCT,
                MetricType.COUNT,
            ];
        case DimensionType.BOOLEAN:
            return [MetricType.COUNT_DISTINCT, MetricType.COUNT];
        default:
            return [];
    }
};

export const CustomAttributeFunctions = [
    MetricType.MIN,
    MetricType.MAX,
    MetricType.COUNT,
    MetricType.COUNT_DISTINCT,
    MetricType.AVERAGE,
    MetricType.MEDIAN,
    MetricType.PERCENTILE,
    MetricType.SUM,
    MetricType.RANDOM_BUCKET,
    MetricType.RANDOM_VALUE,
];

export const getCustomAttributeAllowedDimensions = (
    metricType: MetricType,
): DimensionType[] => {
    switch (metricType) {
        case MetricType.MIN:
            return [
                DimensionType.NUMBER,
                DimensionType.STRING,
                DimensionType.DATE,
                DimensionType.TIMESTAMP,
            ];
        case MetricType.MAX:
            return [
                DimensionType.NUMBER,
                DimensionType.STRING,
                DimensionType.DATE,
                DimensionType.TIMESTAMP,
            ];
        case MetricType.COUNT:
            return [
                DimensionType.NUMBER,
                DimensionType.STRING,
                DimensionType.DATE,
                DimensionType.TIMESTAMP,
                DimensionType.BOOLEAN,
            ];
        case MetricType.COUNT_DISTINCT:
            return [
                DimensionType.NUMBER,
                DimensionType.STRING,
                DimensionType.DATE,
                DimensionType.TIMESTAMP,
            ];
        case MetricType.AVERAGE:
            return [DimensionType.NUMBER];
        case MetricType.MEDIAN:
            return [DimensionType.NUMBER];
        case MetricType.PERCENTILE:
            return [DimensionType.NUMBER];
        case MetricType.SUM:
            return [DimensionType.NUMBER];
        case MetricType.RANDOM_BUCKET:
        case MetricType.RANDOM_VALUE:
            // These can hash any dimension type that can be converted to string
            return [
                DimensionType.NUMBER,
                DimensionType.STRING,
                DimensionType.DATE,
                DimensionType.TIMESTAMP,
            ];
        default:
            return [];
    }
};

export const getBaseDimensionTypeFromMetricType = (
    metric: AdditionalMetric,
    relation: CompiledRelation,
): DimensionType => {
    const baseTable = metric.table;
    const baseDimension = metric.baseDimensionName;

    if (!baseDimension) {
        return DimensionType.NUMBER;
    }

    const baseDimensionType =
        relation.tables[baseTable].dimensions[baseDimension].type;
    return baseDimensionType;
};
